import React, { useMemo, useState } from "react";
import { splitLineItem, money } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Scissors, Plus, Trash2, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

export const SplitLineDialog = ({ invoiceId, line, currency, categories, user, onClose, onDone }) => {
  const [parts, setParts] = useState(() => [
    { key: "a", gl_code: line.gl_code, amount: r2(line.amount / 2), description: "" },
    { key: "b", gl_code: "", amount: r2(line.amount - r2(line.amount / 2)), description: "" },
  ]);
  const [busy, setBusy] = useState(false);

  const total = useMemo(() => r2(parts.reduce((a, p) => a + (Number(p.amount) || 0), 0)), [parts]);
  const remaining = r2(line.amount - total);
  const balanced = Math.abs(remaining) < 0.005;
  const valid = balanced && parts.every((p) => p.gl_code && Number(p.amount) > 0);

  const update = (i, patch) => setParts((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const addPart = () => setParts((ps) => [...ps, { key: `p${Date.now()}`, gl_code: "", amount: r2(Math.max(0, remaining)), description: "" }]);
  const removePart = (i) => setParts((ps) => ps.filter((_, j) => j !== i));
  const fillRemainder = (i) => update(i, { amount: r2((Number(parts[i].amount) || 0) + remaining) });
  const setPct = (i, pct) => update(i, { amount: r2((line.amount * pct) / 100) });

  const submit = async () => {
    setBusy(true);
    try {
      const updated = await splitLineItem(
        invoiceId,
        line.id,
        parts.map((p) => ({ gl_code: p.gl_code, amount: r2(p.amount), description: p.description || null })),
        user
      );
      toast.success(`Line split into ${parts.length} GL codes`);
      onDone(updated);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not split line");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="split-line-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scissors className="h-4 w-4 text-primary" /> Split line across GL codes</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{line.description}</span> · {money(line.amount, currency)}. Divide the amount between
            two or more accounts — the parts must add up exactly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {parts.map((p, i) => (
            <div key={p.key} data-testid={`split-part-${i}`} className="grid gap-2 sm:grid-cols-[1fr_140px_auto] items-center rounded-lg border border-border p-2">
              <Select value={p.gl_code} onValueChange={(v) => update(i, { gl_code: v })}>
                <SelectTrigger data-testid={`split-gl-${i}`} className="font-mono text-xs h-9">
                  <SelectValue placeholder="Choose GL code…" />
                </SelectTrigger>
                <SelectContent className="max-h-[260px]">
                  {categories.map((c) => (
                    <SelectItem key={c.code} value={c.code} className="font-mono text-xs">{c.code} — {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-mono">{currency}</span>
                <Input
                  data-testid={`split-amount-${i}`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={p.amount}
                  onChange={(e) => update(i, { amount: e.target.value })}
                  className="pl-11 font-mono text-sm h-9"
                />
              </div>
              <div className="flex items-center gap-1">
                {[25, 50, 75].map((pct) => (
                  <button key={pct} data-testid={`split-pct-${i}-${pct}`} onClick={() => setPct(i, pct)} className="rounded border border-border px-1.5 py-1 text-[10px] font-mono text-muted-foreground hover:text-foreground hover:bg-muted">
                    {pct}%
                  </button>
                ))}
                {!balanced && (
                  <button data-testid={`split-fill-${i}`} onClick={() => fillRemainder(i)} className="rounded border border-primary/40 px-1.5 py-1 text-[10px] text-primary hover:bg-primary/10">
                    fill
                  </button>
                )}
                <Button data-testid={`split-remove-${i}`} size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={() => removePart(i)} disabled={parts.length <= 2}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <Button data-testid="split-add-part" variant="outline" size="sm" onClick={addPart} disabled={parts.length >= 10}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add part
            </Button>
            <div className={cn("flex items-center gap-2 text-sm font-mono", balanced ? "text-emerald-500" : "text-amber-500")} data-testid="split-balance">
              {balanced ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {money(total, currency)} / {money(line.amount, currency)}
              {!balanced && <span className="text-xs">({remaining > 0 ? "+" : ""}{money(remaining, currency)} left)</span>}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="split-confirm-btn" onClick={submit} disabled={busy || !valid}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Scissors className="h-4 w-4 mr-2" />}
            Split into {parts.length} lines
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
